/* eslint-disable sonarjs/cognitive-complexity */
import { cn, Stack, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GraphTileType } from '@/api/rest/resources/types/widget';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GRAPHS_WITH_CUSTOM_BENCHMARKS } from '@/components/Charts/constants';
import { GRAPH_TYPE, useChartType } from '@/components/Charts/hooks/useChartType';
import {
  INDICATOR_LOADING_STATE,
  IndicatorProvider,
  useHasIndicatorData,
  useSetIndicatorHasData,
} from '@/components/IndicatorCard/IndicatorContextProvider';
import { NCCard, NCCardAnalysisTypeToggle } from '@/components/NCCard/NCCard';
import { useActivePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';

import { ChartEditDropdown } from '../Charts/components/ChartEditDropdown';
import { CustomBenchmarkDialog } from '../Charts/components/CustomBenchmarkDialog';
import { GraphTileProps } from '../IndicatorCard/GraphTile';
import { GetGraphTileByType, IndicatorCardProps } from '../IndicatorCard/IndicatorCard';
import { Icon } from '../widgets/a/Icon/Icon';

export const DetailsCard = (props: IndicatorCardProps) => {
  const activePlotGroup = useActivePlotGroup();
  const propPlotGroupId = props.graphTileProps?.plotGroupId ?? null;

  const plotGroupId = propPlotGroupId ?? activePlotGroup?.id ?? null;
  const plotGroupFilters = activePlotGroup?.filters ?? null;

  const plotGroupKey = JSON.stringify({ id: plotGroupId, filters: plotGroupFilters });
  return (
    <IndicatorProvider key={plotGroupKey} defaultState={{ statsData: false, graphData: INDICATOR_LOADING_STATE }}>
      <DetailsCardContent plotGroupKey={plotGroupKey} {...props} />
    </IndicatorProvider>
  );
};

export const DetailsCardContent = ({
  plotGroupKey,
  headerProps,
  graphTileType,
  graphTileProps,
  styles,
}: IndicatorCardProps & { plotGroupKey: string }) => {
  const { t } = useTranslation();
  const hasCardDataToDisplay = useHasIndicatorData();
  const { setIndicatorGraphData } = useSetIndicatorHasData();
  const { selectedGraphType, setSelectedGraphType } = useChartType();

  useLayoutEffect(() => {
    setIndicatorGraphData({ graphData: INDICATOR_LOADING_STATE });
  }, [plotGroupKey, setIndicatorGraphData]);

  const { zoom, setZoom } = useChartZoom();

  let customBenchmarkIndicator = graphTileType;
  if (graphTileType === GraphTileType.custom) {
    const factFromGraphConfig = graphTileProps?.contentProps?.graphFactType as GraphTileType | undefined;
    if (!factFromGraphConfig) {
      Logger.error(
        `graphFactType is required for custom graph tiles. Widget received: ${JSON.stringify(graphTileProps)}`,
      );
      throw new Error('graphFactType is required for custom graph tiles.');
    }
    customBenchmarkIndicator = factFromGraphConfig;
  }

  const [selectedBenchmark, setSelectedBenchmark] = useBenchmarkSelection(customBenchmarkIndicator as string);
  const [isBenchmarkDialogOpen, setIsBenchmarkDialogOpen] = useState(false);

  const supportsCustomBenchmarks = graphTileType && GRAPHS_WITH_CUSTOM_BENCHMARKS.has(graphTileType);
  const isValidGraphType = graphTileType !== GraphTileType.custom || !!headerProps?.showCustomBenchmark;
  const showCustomBenchmark = supportsCustomBenchmarks && isValidGraphType;

  const graphProps = {
    ...graphTileProps,
    headerProps: {
      ...graphTileProps?.headerProps,
      title: '',
      hideChartHeader: true,
    },
  } as GraphTileProps;

  const showEmpty = graphTileProps?.showEmpty ?? false;

  const showZoom = headerProps?.showZoom && selectedGraphType !== GRAPH_TYPE.BAR;

  if (!hasCardDataToDisplay && !showEmpty) {
    return null;
  }

  const showChartEditDropdown = showZoom || headerProps?.showGraphType || showCustomBenchmark;

  return (
    <NCCard className={cn('p-8', styles?.root)}>
      <Stack
        className={cn(
          'w-full flex flex-col items-center justify-items-center gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr]',
          headerProps?.showZoom ? 'lg:justify-items-between' : 'lg:justify-items-start',
          showChartEditDropdown && !headerProps?.showToggle && 'lg:grid-cols-[auto_1fr]',
        )}
      >
        {headerProps?.title && (
          <Stack spacing={2} className='justify-self-start'>
            <span className='typography-overline uppercase'>
              {isNullish(headerProps?.sectionHeader)
                ? t('shared.ncaDetail.details.historicDevelopment')
                : headerProps?.sectionHeader}
            </span>
            <span className='typography-h4'>
              {headerProps?.title}
              {headerProps?.headerTooltip && (
                <Tooltip>
                  <TooltipTrigger className='items-center'>
                    <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
                  </TooltipTrigger>
                  <TooltipContent className='whitespace-pre-wrap'>{headerProps?.headerTooltip}</TooltipContent>
                </Tooltip>
              )}
            </span>
          </Stack>
        )}
        {headerProps?.showToggle && <NCCardAnalysisTypeToggle data-testid='analysis-type-toggle' />}
        <Stack spacing={4} direction='row' className='justify-self-end' centerMain>
          {showChartEditDropdown && (
            <ChartEditDropdown
              graphType={headerProps?.showGraphType ? selectedGraphType : undefined}
              setGraphType={headerProps?.showGraphType ? setSelectedGraphType : undefined}
              indicator={customBenchmarkIndicator}
              zoom={showZoom ? zoom : undefined}
              setZoom={showZoom ? setZoom : undefined}
              selectedBenchmark={showCustomBenchmark ? selectedBenchmark : undefined}
              setSelectedBenchmark={showCustomBenchmark ? setSelectedBenchmark : undefined}
              onOpenBenchmarkDialog={showCustomBenchmark ? () => setIsBenchmarkDialogOpen(true) : undefined}
            />
          )}
        </Stack>
      </Stack>
      <Stack className='items-center sm:items-stretch' center>
        {graphTileType &&
          GetGraphTileByType(graphTileType, graphProps, zoom, setZoom, selectedGraphType, selectedBenchmark)}
      </Stack>
      {customBenchmarkIndicator && (
        <CustomBenchmarkDialog
          isOpen={isBenchmarkDialogOpen}
          onClose={() => setIsBenchmarkDialogOpen(false)}
          indicatorType={customBenchmarkIndicator}
        />
      )}
    </NCCard>
  );
};
